"use client";

import { useState } from "react";
import { Arrow } from "./Icon";

export interface RequestAssessmentFormLabels {
  contact_info_title: string;
  facility_info_title: string;
  requirements_title: string;
  challenge_section_title: string;
  challenge_help: string;
  challenge_placeholder: string;
  field_first_name: string;
  field_first_name_placeholder: string;
  field_last_name: string;
  field_last_name_placeholder: string;
  field_position: string;
  field_position_placeholder: string;
  field_company: string;
  field_company_placeholder: string;
  field_email: string;
  field_email_placeholder: string;
  field_phone: string;
  field_phone_placeholder: string;
  field_facility_name: string;
  field_facility_name_placeholder: string;
  field_facility_location: string;
  field_facility_location_placeholder: string;
  field_critical_assets: string;
  field_critical_assets_placeholder: string;
  field_industry: string;
  field_industry_placeholder: string;
  field_industry_other_label: string;
  requirements: string[];
  submit_label: string;
  submit_sending: string;
  success_title: string;
  success_subtitle: string;
  success_again_label: string;
  error_generic: string;
}

export default function RequestAssessmentForm({
  industries,
  labels,
}: {
  industries: { slug: string; title: string }[];
  labels: RequestAssessmentFormLabels;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState("");
  const [reqs, setReqs] = useState<string[]>([]);

  function toggle(req: string) {
    setReqs((prev) => (prev.includes(req) ? prev.filter((r) => r !== req) : [...prev, req]));
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    const data = {
      full_name: `${fd.get("first_name")} ${fd.get("last_name")}`,
      job_title: fd.get("position"),
      company: fd.get("company"),
      industry: fd.get("industry"),
      email: fd.get("email"),
      phone: fd.get("phone"),
      message: [
        `Facility: ${fd.get("facility_name")}`,
        `Location: ${fd.get("facility_location")}`,
        `Critical Assets: ${fd.get("critical_assets")}`,
        `Requirements: ${reqs.join(", ")}`,
        "",
        `Challenge: ${fd.get("challenge")}`,
      ].join("\n"),
    };
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || labels.error_generic);
      }
      setStatus("ok");
      form.reset();
      setReqs([]);
    } catch (err: any) {
      setStatus("error");
      setError(err.message);
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center dark:border-emerald-800/60 dark:bg-emerald-950/40">
        <h3 className="text-lg font-bold text-navy dark:text-emerald-100">{labels.success_title}</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-emerald-200/80">{labels.success_subtitle}</p>
        <button onClick={() => setStatus("idle")} className="mt-5 text-sm font-semibold text-brand-red dark:text-brand-red-bright">
          {labels.success_again_label}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-8 lg:grid-cols-3">
      {/* Contact info */}
      <div>
        <h3 className="text-sm font-bold uppercase text-brand-red">{labels.contact_info_title}</h3>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field name="first_name" label={labels.field_first_name} required placeholder={labels.field_first_name_placeholder} />
          <Field name="last_name" label={labels.field_last_name} required placeholder={labels.field_last_name_placeholder} />
          <Field name="position" label={labels.field_position} required placeholder={labels.field_position_placeholder} />
          <Field name="company" label={labels.field_company} required placeholder={labels.field_company_placeholder} />
          <Field name="email" label={labels.field_email} type="email" required placeholder={labels.field_email_placeholder} />
          <Field name="phone" label={labels.field_phone} type="tel" required placeholder={labels.field_phone_placeholder} />
        </div>
      </div>

      {/* Facility info */}
      <div>
        <h3 className="text-sm font-bold uppercase text-navy">{labels.facility_info_title}</h3>
        <div className="mt-4 grid gap-4">
          <Field name="facility_name" label={labels.field_facility_name} placeholder={labels.field_facility_name_placeholder} />
          <Field name="facility_location" label={labels.field_facility_location} placeholder={labels.field_facility_location_placeholder} />
          <Field name="critical_assets" label={labels.field_critical_assets} placeholder={labels.field_critical_assets_placeholder} />
          <div>
            <Label>{labels.field_industry} <Req /></Label>
            <select name="industry" required defaultValue="" className="input">
              <option value="" disabled>{labels.field_industry_placeholder}</option>
              {industries.map((i) => <option key={i.slug} value={i.title}>{i.title}</option>)}
              <option value="Other">{labels.field_industry_other_label}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assessment requirements */}
      <div>
        <h3 className="text-sm font-bold uppercase text-navy">{labels.requirements_title}</h3>
        <div className="mt-4 space-y-2.5">
          {labels.requirements.map((req) => (
            <label key={req} className="flex items-start gap-2.5 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={reqs.includes(req)}
                onChange={() => toggle(req)}
                className="mt-1 h-4 w-4 accent-brand-red"
              />
              <span>{req}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Challenge */}
      <div className="lg:col-span-3">
        <h3 className="text-sm font-bold uppercase text-brand-red">{labels.challenge_section_title}</h3>
        <p className="mt-1 text-xs text-gray-500">{labels.challenge_help}</p>
        <textarea
          name="challenge"
          rows={4}
          placeholder={labels.challenge_placeholder}
          className="input mt-3 resize-none"
        />
      </div>

      {status === "error" && <p className="lg:col-span-3 text-sm text-brand-red">{error}</p>}

      <div className="lg:col-span-3 flex justify-center">
        <button type="submit" disabled={status === "sending"} className="btn-red px-10 disabled:opacity-60">
          {status === "sending" ? labels.submit_sending : <>{labels.submit_label} <Arrow /></>}
        </button>
      </div>
    </form>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="mb-1.5 block text-sm font-semibold text-navy">{children}</label>;
}
function Req() { return <span className="text-brand-red">*</span>; }
function Field({ name, label, type = "text", required = false, placeholder }: { name: string; label: string; type?: string; required?: boolean; placeholder?: string }) {
  return (
    <div>
      <Label>{label} {required && <Req />}</Label>
      <input name={name} type={type} required={required} placeholder={placeholder} className="input" />
    </div>
  );
}
